// Strict hex forms only: #rgb, #rgba, #rrggbb, #rrggbbaa.
const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

// Functional forms rgb()/rgba()/hsl()/hsla(). Anchored, and the body allows
// ONLY digits, dots, commas, %, whitespace and the `/` alpha separator — never
// `;`, `}`, `(`, `url(`, `@`, so nothing can break out of the declaration.
// Deliberately permissive on numeric correctness (the browser ignores an
// out-of-range channel); the point here is injection-safety, not CSS linting.
const FUNCTIONAL_COLOR = /^(rgb|rgba|hsl|hsla)\([\d.,%/\s]*\)$/i

// Small allowlist of CSS named colors teams actually use. Kept deliberately
// short — anything outside hex + this set is rejected rather than trusted.
const NAMED_COLORS = new Set([
  'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple',
  'pink', 'gray', 'grey', 'silver', 'gold', 'navy', 'teal', 'cyan', 'magenta',
  'maroon', 'olive', 'lime', 'aqua', 'fuchsia', 'transparent',
])

/**
 * True only for a value safe to interpolate into a CSS declaration: a strict
 * hex literal or an allowlisted named color. Everything else — `rgb()`/`hsl()`
 * functional forms, and any payload carrying `;`, `}`, `(`, `url(`, `@import`
 * — is rejected, closing the team-branding CSS-injection sink at its choke
 * point (`toTeamTheme`). Not a general CSS-color parser: it errs strict.
 */
export function isValidCssColor(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const v = value.trim()
  if (!v) return false
  if (HEX_COLOR.test(v)) return true
  if (FUNCTIONAL_COLOR.test(v)) return true
  return NAMED_COLORS.has(v.toLowerCase())
}

export function hexToRgba(hex: string, opacity: number = 1): string {
  // Remove the hash symbol when present
  const cleanHex = hex?.replace?.('#', '');

  // Validate HEX format
  if (!/^([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(cleanHex)) {
    return '';
  }

  // If the HEX is shorthand (e.g., #FFF), expand it to full form (#FFFFFF)
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map((char) => char + char).join('')
    : cleanHex;

  // Extract the RGB values
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Return the RGBA string
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
